import { isAxiosError } from 'axios';
import api from './api';

/** Registro normalizado para exibição (aceita vários formatos vindos do backend). */
export interface ActivityLog {
  id: number | string;
  createdAt: string;
  userId?: number;
  userName?: string;
  userEmail?: string;
  action: string;
  description?: string;
  subjectType?: string;
  subjectId?: string | number;
  ipAddress?: string;
  metadata?: Record<string, unknown>;
}

export interface ActivityLogsPageResult {
  logs: ActivityLog[];
  page: number;
  lastPage: number;
  total: number;
}

export interface FetchActivityLogsParams {
  page?: number;
  perPage?: number;
  userId?: number;
  search?: string;
  dateFrom?: string;
  dateTo?: string;
}

function pickStr(v: unknown): string | undefined {
  if (v == null) return undefined;
  const s = String(v).trim();
  return s || undefined;
}

function pickNum(v: unknown): number | undefined {
  if (v == null || v === '') return undefined;
  const n = Number(v);
  return Number.isFinite(n) ? n : undefined;
}

function obj(v: unknown): Record<string, unknown> | null {
  return v && typeof v === 'object' && !Array.isArray(v) ? (v as Record<string, unknown>) : null;
}

/** Mapeia uma linha da API (Spatie Activity, modelo próprio ou genérico). */
export function normalizeActivityRow(r: Record<string, unknown>): ActivityLog {
  const causer = obj(r.causer);
  const user = obj(r.user);
  const props = obj(r.properties) ?? obj(r.context) ?? obj(r.meta);

  const userName =
    pickStr(r.user_name) ??
    pickStr(r.userName) ??
    (causer ? pickStr(causer.name) : undefined) ??
    (user ? pickStr(user.name) : undefined);

  const userEmail =
    pickStr(r.user_email) ??
    pickStr(r.userEmail) ??
    (causer ? pickStr(causer.email) : undefined) ??
    (user ? pickStr(user.email) : undefined);

  const userId =
    pickNum(r.user_id) ??
    pickNum(r.userId) ??
    pickNum(r.causer_id) ??
    (causer ? pickNum(causer.id) : undefined) ??
    (user ? pickNum(user.id) : undefined);

  const action =
    pickStr(r.action) ??
    pickStr(r.event) ??
    pickStr(r.log_name) ??
    pickStr(r.tipo) ??
    'ação';

  const description =
    pickStr(r.description) ??
    pickStr(r.mensagem) ??
    pickStr(r.message) ??
    pickStr(r.note);

  const subjectType = pickStr(r.subject_type) ?? pickStr(r.subjectType) ?? pickStr(r.entity_type);
  const subjectId = r.subject_id ?? r.subjectId ?? r.entity_id;

  const idRaw = r.id ?? r.log_id;
  const id: string | number =
    typeof idRaw === 'string' || typeof idRaw === 'number' ? idRaw : `${r.created_at ?? ''}-${action}`;

  return {
    id,
    createdAt: pickStr(r.created_at) ?? pickStr(r.createdAt) ?? '',
    userId,
    userName,
    userEmail,
    action,
    description,
    subjectType,
    subjectId: subjectId as string | number | undefined,
    ipAddress: pickStr(r.ip_address) ?? pickStr(r.ip),
    metadata: props ?? undefined,
  };
}

function extractPagePayload(body: unknown): { rows: Record<string, unknown>[]; page: number; lastPage: number; total: number } {
  if (Array.isArray(body)) {
    return {
      rows: body.filter(Boolean) as Record<string, unknown>[],
      page: 1,
      lastPage: 1,
      total: body.length,
    };
  }
  if (!body || typeof body !== 'object') {
    return { rows: [], page: 1, lastPage: 1, total: 0 };
  }
  const o = body as Record<string, unknown>;
  const meta = obj(o.meta);
  const list = o.data;

  if (Array.isArray(list)) {
    return {
      rows: list.filter((x) => x && typeof x === 'object') as Record<string, unknown>[],
      page: Number(meta?.current_page ?? o.current_page ?? 1),
      lastPage: Number(meta?.last_page ?? o.last_page ?? 1),
      total: Number(meta?.total ?? o.total ?? list.length),
    };
  }

  /* Alguns backends devolvem { logs: [...] } */
  const logs = o.logs;
  if (Array.isArray(logs)) {
    return {
      rows: logs.filter((x) => x && typeof x === 'object') as Record<string, unknown>[],
      page: Number(o.current_page ?? 1),
      lastPage: Number(o.last_page ?? 1),
      total: Number(o.total ?? logs.length),
    };
  }

  return { rows: [], page: 1, lastPage: 1, total: 0 };
}

const activityLogService = {
  /**
   * Lista atividades registradas no servidor para a empresa atual (header company-x).
   * Endpoint esperado: GET /api/activity-logs
   * Query: page, per_page, user_id, search, date_from, date_to (snake_case, comum em Laravel).
   */
  async fetchLogs(params: FetchActivityLogsParams = {}): Promise<ActivityLogsPageResult> {
    const query: Record<string, string | number> = {};
    const page = params.page ?? 1;
    const perPage = params.perPage ?? 30;
    query.page = page;
    query.per_page = perPage;
    if (params.userId != null && params.userId > 0) query.user_id = params.userId;
    if (params.search?.trim()) query.search = params.search.trim();
    if (params.dateFrom?.trim()) query.date_from = params.dateFrom.trim();
    if (params.dateTo?.trim()) query.date_to = params.dateTo.trim();

    try {
      const response = await api.get('/activity-logs', { params: query });
      const { rows, page: pg, lastPage, total } = extractPagePayload(response.data);
      return {
        logs: rows.map((row) => normalizeActivityRow(row)),
        page: pg || 1,
        lastPage: Math.max(1, lastPage || 1),
        total,
      };
    } catch (e: unknown) {
      if (isAxiosError(e) && e.response?.status === 404) {
        const err = new Error(
          'ENDPOINT_MISSING: cadastre no backend GET /api/activity-logs (admin) ou ajuste a rota no activityLogService.'
        );
        (err as Error & { code?: string }).code = 'ENDPOINT_MISSING';
        throw err;
      }
      throw e;
    }
  },
};

export default activityLogService;
